"use client";

import { useQuery } from "@tanstack/react-query";

import { useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { HiOutlineDocument } from "react-icons/hi";

import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import ScanControls from "@/components/patient/documents/scan-controls";
import { Card } from "@/components/ui";
import Button from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { getFileIcon } from "@/lib/file-utils";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";

import type { DocumentSummary } from "@/types/document";

import {
  useUnlinkDocumentFromNoteMutation,
  useUploadNoteDocumentMutation,
} from "@/api/provider/scribe/note-mutation";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { documentTypeQueryOptions } from "@/api/provider/user/patient/document-type-query";

interface NoteDocumentsSectionProps {
  noteId: string;
  disabled?: boolean;
  label?: React.ReactNode;
}

export default function NoteDocumentsSection({
  noteId,
  disabled = false,
  label,
}: NoteDocumentsSectionProps) {
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentSummary | null>(null);
  const [documentViewDialogOpen, setDocumentViewDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: documents = [], isPending } = useQuery(
    noteQueryOptions.noteDocuments(noteId),
  );
  const { data: documentTypes = [] } = useQuery(
    documentTypeQueryOptions.list(),
  );

  const uploadMutation = useUploadNoteDocumentMutation(noteId);
  const unlinkMutation = useUnlinkDocumentFromNoteMutation(noteId);

  const getDefaultDocType = () =>
    documentTypes.find((t) => t.name === "Other")?.name ??
    documentTypes[0]?.name ??
    "Other";

  const handleScannedFile = async (file: File) => {
    const title = file.name.replace(/\.[^/.]+$/, "");
    setIsUploading(true);
    try {
      await uploadMutation.mutateAsync({
        file,
        title,
        docType: getDefaultDocType(),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const defaultDocType = getDefaultDocType();
      const uploadPromises = Array.from(files).map((file) => {
        const title = file.name.replace(/\.[^/.]+$/, "");
        return uploadMutation.mutateAsync({
          file,
          title,
          docType: defaultDocType,
        });
      });
      await Promise.allSettled(uploadPromises);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleViewDocument = (document: DocumentSummary) => {
    setSelectedDocument(document);
    setDocumentViewDialogOpen(true);
  };

  const handleUnlink = async (e: React.MouseEvent, documentId: string) => {
    e.stopPropagation();
    await unlinkMutation.mutateAsync(documentId);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        {label ? <div>{label}</div> : <div />}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          multiple
          onChange={handleFileSelect}
          disabled={disabled}
        />
        <div className="flex items-center gap-2">
          <ScanControls
            onFile={handleScannedFile}
            disabled={disabled || isUploading}
            variant="compact"
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading}
            icon={<FiPlus className="mr-1" />}
            className="px-2 py-1 text-caption"
          >
            {isUploading ? "Uploading..." : "Upload Document"}
          </Button>
        </div>
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border p-4">
              <div className="flex items-start space-x-3">
                <Skeleton className="h-8 w-8 flex-shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-2 border-dashed p-6 text-center">
          <HiOutlineDocument className="mx-auto mb-2 h-8 w-8 text-foreground-muted" />
          <p className="text-body-strong text-foreground-secondary">
            No documents attached
          </p>
          <p className="mt-1 text-caption text-foreground-muted">
            Upload or link documents from the patient&apos;s documents tab
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {documents.map((document) => (
            <div
              key={document.id}
              className="group relative cursor-pointer rounded-lg border p-3 transition-shadow hover:shadow-sm"
              onClick={() => handleViewDocument(document)}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getFileIcon(document.fileType)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body-strong text-foreground">
                    {document.title}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2 text-caption text-foreground-muted">
                    <span>{formatToHumanReadable(document.docType)}</span>
                    {document.createdDate && (
                      <>
                        <span>·</span>
                        <span>{formatDate(document.createdDate)}</span>
                      </>
                    )}
                  </div>
                </div>
                {!disabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<FiX className="h-4 w-4 text-foreground-muted" />}
                    className="h-7 w-7 flex-shrink-0 p-0 opacity-0 group-hover:opacity-100"
                    onClick={(e) => handleUnlink(e, document.id)}
                    tooltip="Remove from note"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentViewDialog
        isOpen={documentViewDialogOpen}
        onOpenChange={setDocumentViewDialogOpen}
        document={selectedDocument}
      />
    </div>
  );
}
